import { uniqueId } from "@lernetz/svelte-common";
import { listen, setAttribute, trackAttribute, unify } from "@lernetz/svelte-common/actions";
import { get, writable } from "svelte/store";

interface OverlayStore {
  opened: boolean;
  id: string;
}

export type Overlay = ReturnType<typeof createOverlay>;

export type OverlayHtmlType = "menu" | "listbox" | "dialog" | "tree" | "grid" | "dialog";

export function createOverlay(type: OverlayHtmlType = "menu", group?: string) {
  const store = writable<OverlayStore>({ opened: false, id: uniqueId("overlay") });
  const { set, update, subscribe } = store;

  let triggerReference: HTMLElement;

  const open = () => update((s) => ({ ...s, opened: true }));
  const close = () => update((s) => ({ ...s, opened: false }));
  const toggle = () => update((s) => ({ ...s, opened: !s.opened }));

  if (group) {
    subscribe((s) => {
      if (s.opened) {
        setCurrentOverlayInGroup(group, { id: s.id, close });
      }
    });
  }

  function trigger(node: HTMLElement, options?: { handler?: (event: MouseEvent) => void }) {
    triggerReference = node;

    let handler = options?.handler || toggle;

    return unify([
      setAttribute(node, {
        role: "button",
        type: "button",
        "aria-haspopup": type,
        "aria-controls": get(store).id,
        id: uniqueId("trigger"),
      }),
      trackAttribute(node, { attribute: "aria-expanded", store, value: (s) => s.opened + "" }),
      listen(node, {
        type: "click",
        handler: handler,
      }),
    ]);
  }

  function autoclose(node: HTMLElement) {
    return listen(node, { type: "click", handler: close });
  }

  return {
    open,
    close,
    toggle,
    trigger,
    autoclose,
    triggerReference: () => triggerReference,
    subscribe,
    set,
    update,
  };
}

export type OverlayType = ReturnType<typeof createOverlay>;

// The following infrastructure keeps track of each overlay group's current overlay,
// so that it can be auto-closed when another overlay from the same group gets opened.

interface OverlayCloseInfo {
  id: string;
  close: () => void;
}

const groups = new Map<string, OverlayCloseInfo>();

function setCurrentOverlayInGroup(group: string, current: OverlayCloseInfo): void {
  const previous = groups.get(group);
  if (previous && previous.id !== current.id) {
    previous.close();
  }
  groups.set(group, current);
}
